/**
 * DiffModal
 *
 * Displays a structural diff between two taxonomy versions.
 * Shows dimension, category, and label changes with old/new values.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { ScrollArea } from '../../../../ui/scroll-area';
import { useVersionDiff } from '../../hooks/useTaxonomy';
import ChangeSummaryBadges from '../../shared/ChangeSummaryBadges';
import type { ChangeType } from '../../types/taxonomy';

const CHANGE_COLORS: Record<ChangeType, string> = {
  added: 'border-green-200 bg-green-50',
  removed: 'border-red-200 bg-red-50',
  modified: 'border-blue-200 bg-blue-50',
  moved: 'border-purple-200 bg-purple-50',
};

const CHANGE_LABELS: Record<ChangeType, string> = {
  added: 'Added',
  removed: 'Removed',
  modified: 'Modified',
  moved: 'Moved',
};

interface DiffModalProps {
  taxonomyKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionAId: string;
  versionBId: string;
  versionALabel: string;
  versionBLabel: string;
}

const DiffModal: React.FC<DiffModalProps> = ({
  taxonomyKey,
  open,
  onOpenChange,
  versionAId,
  versionBId,
  versionALabel,
  versionBLabel,
}) => {
  const { data: diff, isLoading } = useVersionDiff(taxonomyKey, versionAId, versionBId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            Diff: {versionALabel} → {versionBLabel}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-gray-500">Loading diff...</div>
        ) : diff ? (
          <div className="space-y-4">
            {/* Summary badges */}
            <ChangeSummaryBadges summary={diff.summary} level="all" />

            <ScrollArea className="h-[50vh]">
              <div className="space-y-3 pr-4">
                {/* Dimension changes */}
                {diff.dimensionChanges.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Dimensions
                    </h4>
                    {diff.dimensionChanges.map((dc) => (
                      <div
                        key={`dim-${dc.dimensionId}-${dc.changeType}`}
                        className={`border rounded-md p-3 mb-2 ${CHANGE_COLORS[dc.changeType]}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{CHANGE_LABELS[dc.changeType]}</span>
                          <span className="font-medium text-sm">{dc.dimensionName}</span>
                        </div>
                        {dc.oldValues && dc.newValues && (
                          <FieldDiff oldValues={dc.oldValues} newValues={dc.newValues} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Category changes */}
                {diff.categoryChanges.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Categories
                    </h4>
                    {diff.categoryChanges.map((cc) => (
                      <div
                        key={`cat-${cc.categoryId}-${cc.changeType}`}
                        className={`border rounded-md p-3 mb-2 ${CHANGE_COLORS[cc.changeType]}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{CHANGE_LABELS[cc.changeType]}</span>
                          <span className="font-medium text-sm">{cc.categoryName}</span>
                          <span className="text-xs text-gray-500">in {cc.dimensionId}</span>
                        </div>
                        {cc.oldValues && cc.newValues && (
                          <FieldDiff oldValues={cc.oldValues} newValues={cc.newValues} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Label changes */}
                {diff.labelChanges.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Labels
                    </h4>
                    {diff.labelChanges.map((lc) => (
                      <div
                        key={`lbl-${lc.labelId}-${lc.changeType}`}
                        className={`border rounded-md p-3 mb-2 ${CHANGE_COLORS[lc.changeType]}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{CHANGE_LABELS[lc.changeType]}</span>
                          <span className="font-medium text-sm">{lc.labelName}</span>
                          <span className="text-xs text-gray-500">
                            {lc.dimensionId} / {lc.categoryId}
                          </span>
                        </div>
                        {lc.movedFrom && (
                          <p className="text-xs text-purple-600 mt-1">
                            Moved from {lc.movedFrom.dimension} / {lc.movedFrom.category}
                          </p>
                        )}
                        {lc.oldValues && lc.newValues && (
                          <FieldDiff oldValues={lc.oldValues} newValues={lc.newValues} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {diff.dimensionChanges.length === 0 &&
                  diff.categoryChanges.length === 0 &&
                  diff.labelChanges.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No structural differences found.
                    </p>
                  )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Failed to load diff.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Renders old → new field-level changes.
 */
const FieldDiff: React.FC<{
  oldValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
}> = ({ oldValues, newValues }) => {
  const fields = Object.keys({ ...oldValues, ...newValues });
  if (fields.length === 0) return null;

  return (
    <div className="mt-2 space-y-1">
      {fields.map((field) => (
        <div key={field} className="text-xs">
          <span className="font-medium text-gray-600">{field}:</span>{' '}
          <span className="text-red-600 line-through">
            {String(oldValues[field] ?? '')}
          </span>{' '}
          → <span className="text-green-600">{String(newValues[field] ?? '')}</span>
        </div>
      ))}
    </div>
  );
};

export default DiffModal;
